import { useCallback, useState } from 'react';
import { isNewFeatureForUser, type FeatureBadgeId } from '../utils/featureBadges';
import { useUserSettings } from './useUserSettings';

export const DISMISSED_FEATURE_BADGES_KEY = 'sse_dismissed_badges';

function readDismissedBadges(): Record<string, boolean> {
  try {
    const saved = localStorage.getItem(DISMISSED_FEATURE_BADGES_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
}

export function useFeatureBadges() {
  const { data: userSettingsData } = useUserSettings();
  const [dismissedBadges, setDismissedBadges] = useState<Record<string, boolean>>(readDismissedBadges);

  const dismissBadge = useCallback((featureId: FeatureBadgeId) => {
    setDismissedBadges((current) => {
      if (current[featureId]) return current;
      const nextDismissed = { ...current, [featureId]: true };
      try {
        localStorage.setItem(DISMISSED_FEATURE_BADGES_KEY, JSON.stringify(nextDismissed));
      } catch (error) {
        console.error('Failed to save dismissed badge state', error);
      }
      return nextDismissed;
    });
  }, []);

  const shouldShowNewBadge = useCallback((featureId: FeatureBadgeId) => (
    isNewFeatureForUser(featureId, userSettingsData?.createdAt)
    && !dismissedBadges[featureId]
  ), [dismissedBadges, userSettingsData?.createdAt]);

  const shouldShowNavNewBadge = useCallback((item: { badge?: string; featureBadgeId?: FeatureBadgeId }) => (
    item.badge === 'New'
    && !!item.featureBadgeId
    && shouldShowNewBadge(item.featureBadgeId)
  ), [shouldShowNewBadge]);

  return { dismissBadge, shouldShowNewBadge, shouldShowNavNewBadge };
}
